import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { toast } from 'sonner'
import { formatMediumDate } from '@/utils/date'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface BrandRegistrationItem {
  registration_id: string
  firm_name: string
  brand_key: string
  submitted_by: string
  firm_id: string | null
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  custom_domain: string | null
  status: 'pending' | 'approved' | 'rejected'
  reviewed_by: string | null
  review_note: string | null
  submitted_at: string | null
  reviewed_at: string | null
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {status}
    </span>
  )
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return formatMediumDate(iso)
}

export function BrandRegistrationsPage() {
  const { data, loading, error, execute } = useApi<BrandRegistrationItem[]>()

  const load = useCallback(() => {
    execute(() => apiClient.get<BrandRegistrationItem[]>('/v1/admin/brand-registrations').then(r => r.data))
  }, [execute])

  useEffect(() => {
    load()
  }, [load])

  const [approveId, setApproveId] = useState<string | null>(null)
  const [rejectId, setRejectId] = useState<string | null>(null)
  const [rejectNote, setRejectNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleApprove() {
    if (!approveId) return
    setSubmitting(true)
    try {
      await apiClient.patch(`/v1/admin/brand-registrations/${approveId}/approve`)
      toast.success('Brand approved and live.')
      setApproveId(null)
      load()
    } catch {
      toast.error('Failed to approve registration.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleReject() {
    if (!rejectId) return
    setSubmitting(true)
    try {
      await apiClient.patch(`/v1/admin/brand-registrations/${rejectId}/reject`, {
        note: rejectNote,
      })
      toast.success('Registration rejected.')
      setRejectId(null)
      setRejectNote('')
      load()
    } catch {
      toast.error('Failed to reject registration.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading brand registrations…</div>
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load registrations.</div>

  const registrations = data ?? []

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Brand Registration Approval</h1>
          <p className="text-sm text-gray-500 mt-1">{registrations.length} submitted for review</p>
        </div>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      {registrations.length === 0 ? (
        <p className="text-sm text-gray-500">
          Nothing awaiting approval. Registrations appear here after a firm submits
          its brand on the Register a Brand page.
        </p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Brand Key</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead>Colors</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.map(r => (
                <TableRow key={r.registration_id}>
                  <TableCell className="text-sm whitespace-nowrap">
                    {fmtDate(r.submitted_at)}
                  </TableCell>
                  <TableCell className="font-medium text-sm">
                    <div className="flex items-center gap-2">
                      {r.logo_url && (
                        <img src={r.logo_url} alt={r.firm_name} className="h-5 w-5 object-contain" />
                      )}
                      {r.firm_name}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    <code>{r.brand_key}</code>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{r.submitted_by}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {r.primary_color && (
                        <span
                          className="h-4 w-4 rounded-full border border-gray-200 inline-block"
                          style={{ backgroundColor: r.primary_color }}
                          title={r.primary_color}
                        />
                      )}
                      {r.secondary_color && (
                        <span
                          className="h-4 w-4 rounded-full border border-gray-200 inline-block"
                          style={{ backgroundColor: r.secondary_color }}
                          title={r.secondary_color}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{r.custom_domain ?? '—'}</TableCell>
                  <TableCell>
                    {r.status === 'rejected' ? (
                      <div>
                        {statusBadge(r.status)}
                        <div className="text-xs text-gray-500 mt-0.5">{fmtDate(r.reviewed_at)}</div>
                      </div>
                    ) : (
                      statusBadge(r.status)
                    )}
                  </TableCell>
                  <TableCell>
                    {r.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => setApproveId(r.registration_id)}>
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRejectId(r.registration_id)
                            setRejectNote('')
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Approve dialog */}
      <Dialog open={!!approveId} onOpenChange={open => !open && setApproveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Brand Registration</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will make the brand live immediately — <code>?brand=</code> will resolve for
            this firm right away. Confirm?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveId(null)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={submitting}>
              {submitting ? 'Approving…' : 'Confirm Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectId} onOpenChange={open => !open && setRejectId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Brand Registration</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              You may include an optional reason for the requester.
            </p>
            <Textarea
              placeholder="Reason (optional)"
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={submitting}
            >
              {submitting ? 'Rejecting…' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
